<template>
  <PageBreadcrumbs :items="breadcrumbs" />
  <ContentCard>
    <template #header>
      <ContentCardSubheader>
        {{ t('ProductTop100Ranking.title', { year: rankingYear }) }}
        <template #actions>
          <RouterLink
            class="btn-primary-color-s"
            data-testid="top-products-ranking-create-campaign"
            :to="{ name: ReviewsRouteName.ReviewsProductCampaignNew, params: { productId: product.id }, query: { occasion: 'top100' } }"
            @click="track('campaign_create_start', { surface: 'cta', entity_type: 'tool', entity_id: product.id })"
          >
            {{ t('ProductTop100Ranking.createCampaign') }}
          </RouterLink>
        </template>
      </ContentCardSubheader>
    </template>
    <div class="flex flex-col gap-6 py-6">
      <p class="max-w-prose text-body-m" data-testid="top-products-ranking-intro">
        {{ t('ProductTop100Ranking.intro', { rankingYear, reviewYear: rankingYear - 1 }) }}
        <a
          :href="t('ProductTop100Ranking.collectingTipsUrl')"
          target="_blank"
          class="link-inline-color"
          data-testid="top-products-ranking-collecting-tips"
          >{{ t('ProductTop100Ranking.collectingTips') }}</a
        >
      </p>
      <MessageBanner type="neutral" data-testid="top-products-ranking-notice">{{ t('ProductTop100Ranking.notice') }}</MessageBanner>
      <div class="flex flex-col gap-2">
        <div class="flex flex-wrap items-center justify-between gap-4">
          <div class="w-full tablet:max-w-sm">
            <TopProductsRankingSearch :model-value="search" @update:model-value="onSearch" />
          </div>
          <button
            v-if="activeTab === 'ranking'"
            type="button"
            class="btn-secondary-color-s"
            :disabled="!ownEntry"
            data-testid="top-products-ranking-my-position"
            @click="onMyPosition"
          >
            {{ t('ProductTop100Ranking.myPosition') }}
          </button>
        </div>
        <div class="max-w-sm">
          <TabBar>
            <TabBarItem
              :model-value="activeTab"
              selected-value="ranking"
              data-testid="top-products-ranking-tab-ranking"
              @update:model-value="onTabChange"
            >
              {{ t('ProductTop100Ranking.compare.tabRanking') }}
            </TabBarItem>
            <TabBarItem
              :model-value="activeTab"
              selected-value="compare"
              data-testid="top-products-ranking-tab-compare"
              @update:model-value="onTabChange"
            >
              {{ t('ProductTop100Ranking.compare.tab') }}
              <span
                class="inline-flex min-w-5 items-center justify-center rounded-full bg-primary-subtle px-1.5 txt-label-s-bold text-primary tabular-nums"
                data-testid="top-products-ranking-compare-count"
                >{{ compare.count.value }}</span
              >
            </TabBarItem>
          </TabBar>
        </div>
        <TopProductsRankingCompareView
          v-if="activeTab === 'compare'"
          :own-entry="ownEntry"
          :selection="compare.selection.value"
          @remove="onCompareRemove"
        />
        <MessageBanner v-else-if="error" type="error" data-testid="top-products-ranking-error">
          {{ t('ProductTop100Ranking.error') }}
        </MessageBanner>
        <template v-else>
          <p v-if="!loading && !ownEntry" class="txt-body-s-regular text-subtle" data-testid="top-products-ranking-unranked">
            {{ t('ProductTop100Ranking.table.unranked') }}
          </p>
          <div class="relative">
            <ProgressBar v-if="loading" class="absolute inset-x-0 top-0" />
            <TopProductsRankingTable
              :entries="entries"
              :own-entry="ownEntry"
              :sort-by="sortBy"
              :sort-direction="sortDirection"
              :search-term="search"
              compare-enabled
              :is-selected="compare.isSelected"
              @sort="onSort"
              @compare-add="onCompareAdd"
              @compare-remove="onCompareRemove"
            />
          </div>
          <PaginationControl
            :page="page"
            :per-page="perPage"
            :total="total"
            :per-page-options="[10, 25, 50]"
            show-pagination="always"
            @update:page="setPage"
            @update:per-page="onPerPage"
          />
          <p class="text-body-s text-subtle" data-testid="top-products-ranking-data-as-of">{{ t('ProductTop100Ranking.dataAsOf') }}</p>
        </template>
      </div>
    </div>
  </ContentCard>
  <div class="mt-6">
    <TopProductsRankingOutdatedReviews :product-id="product.id" :product-slug="product.slug" />
  </div>
</template>

<script setup lang="ts">
import { MessageBanner } from '@ramp106/omrjs-core-ui'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { computed, ref, toRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import PageBreadcrumbs from '@/components/PageBreadcrumbs.vue'
import PaginationControl from '@/components/PaginationControl.vue'
import { useReviewsProductBreadcrumbs } from '@/composables/breadcrumbs/useReviewsProductBreadcrumbs'
import type { ManageTopProductsRankingOrderBy, ProductById } from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper'
import { ReviewsRouteName } from '@/router/types'
import ContentCard from '@/ui/ContentCard.vue'
import ContentCardSubheader from '@/ui/ContentCardSubheader.vue'
import ProgressBar from '@/ui/ProgressBar.vue'
import type { SortDirection, SortParams } from '@/ui/TableLight/types'

import { guideYear } from './collectionDeadline'
import TopProductsRankingCompareView from './TopProductsRankingCompareView.vue'
import TopProductsRankingOutdatedReviews from './TopProductsRankingOutdatedReviews.vue'
import TopProductsRankingSearch from './TopProductsRankingSearch.vue'
import TopProductsRankingTable from './TopProductsRankingTable.vue'
import type { TopProductsRankingEntry } from './types'
import { useTopProductsRanking } from './useTopProductsRanking'
import { useTopProductsRankingCompare } from './useTopProductsRankingCompare'

const props = defineProps<{ product: ProductById }>()

const { t } = useI18n()
const breadcrumbs = useReviewsProductBreadcrumbs(props.product)
const {
  entries,
  ownEntry,
  total,
  year,
  page,
  perPage,
  sortBy,
  sortDirection,
  search,
  loading,
  error,
  setPage,
  setPerPage,
  setSort,
  setSearch,
  goToOwnRow,
} = useTopProductsRanking(toRef(props.product, 'slug'))

const rankingYear = computed(() => (year.value ? year.value + 1 : guideYear(new Date())))

const FILTER_GROUP = 'manage_tool_top_products_ranking'

const compare = useTopProductsRankingCompare(toRef(props.product, 'slug'))
const activeTab = ref<'ranking' | 'compare'>('ranking')

const pendingSearchSwitch = ref(false)

watch(entries, (visible) => {
  compare.refresh(visible)
  if (pendingSearchSwitch.value) {
    pendingSearchSwitch.value = false
    activeTab.value = 'ranking'
  }
})

watch(error, (failed) => {
  if (failed) pendingSearchSwitch.value = false
})

function onTabChange(tab: string) {
  if (tab === 'compare' && activeTab.value !== 'compare') {
    track('cta_click', { surface: 'cta', category: 'engagement', cta_id: 'top_products_ranking_compare_tab' })
  }
  activeTab.value = tab as 'ranking' | 'compare'
}

function trackComparison(action: 'add' | 'remove', comparedTool: string) {
  track('comparison_update', {
    surface: 'cta',
    entity_type: 'tool',
    entity_id: props.product.id,
    action,
    compared_entity_type: 'tool',
    compared_entity_id: comparedTool,
    comparison_size: compare.count.value,
  })
}

function onCompareAdd(entry: TopProductsRankingEntry) {
  if (!compare.add(entry)) return
  trackComparison('add', entry.tool)
  showNotification(t('ProductTop100Ranking.compare.addedToast', { tool: entry.toolTitle ?? entry.tool }), 'success')
}

function onCompareRemove(tool: string) {
  const removed = compare.selection.value.find((selected) => selected.tool === tool)
  compare.remove(tool)
  trackComparison('remove', tool)
  showNotification(t('ProductTop100Ranking.compare.removedToast', { tool: removed?.toolTitle ?? tool }), 'success')
}

function onSearch(term: string) {
  if (term) track('search', { surface: 'search', search_term: term })
  pendingSearchSwitch.value = !!term && activeTab.value === 'compare'
  setSearch(term)
}

function onMyPosition() {
  track('cta_click', { surface: 'cta', category: 'engagement', cta_id: 'top_products_ranking_my_position' })
  goToOwnRow()
}

function onSort(params: SortParams<ManageTopProductsRankingOrderBy, SortDirection>) {
  track('filter_apply', {
    surface: 'filter',
    filter_group: FILTER_GROUP,
    filter_type: 'button',
    filter_name: params.sortBy.toLowerCase(),
    filter_value: params.sortDirection ?? 'asc',
  })
  setSort(params)
}

function onPerPage(value: number) {
  track('filter_apply', {
    surface: 'filter',
    filter_group: FILTER_GROUP,
    filter_type: 'dropdown',
    filter_name: 'per_page',
    filter_value: String(value),
  })
  setPerPage(value)
}
</script>
